// apps/web/src/pages/CheckIns.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogContent,
  Skeleton,
  Button,
} from '@mui/material';
import {
  QrCode,
  ArrowBackIosNew,
  ArrowForwardIos,
  Search,
  Close,
  FilterList,
  Clear,
} from '@mui/icons-material';
import { format, addDays, subDays, isValid } from 'date-fns';
import { ru } from 'date-fns/locale';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { PaginationBar } from '../components/common/PaginationBar';
import { apiClient } from '../api/client';
import { VisitWithVisitor, Visitor } from '@gym-management/shared';
import { getImageSrc } from '../utils/photoUtils';
import VisitorCard from '../components/visitors/VisitorCard';

const PAGE_SIZES = [20, 50, 100];

// Безопасное форматирование даты
const safeFormatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (!isValid(d)) return '—';
  return format(d, 'dd.MM.yyyy');
};

const safeFormatTime = (date: Date | string | null | undefined): string => {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (!isValid(d)) return '—';
  return format(d, 'HH:mm');
};

const VisitHistory: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const filterAppliedRef = useRef(false);

  const [visits, setVisits] = useState<VisitWithVisitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVisitorForFilter, setSelectedVisitorForFilter] = useState<Visitor | null>(null);

  const [activeVisits, setActiveVisits] = useState<Record<string, boolean>>({});
  const [activeSubscriptions, setActiveSubscriptions] = useState<Record<string, boolean>>({});

  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [visitorDetailOpen, setVisitorDetailOpen] = useState(false);
  const [loadingVisitorCard, setLoadingVisitorCard] = useState(false);

  // ------------------------------------------------------------------
  // Загрузка посещений
  // ------------------------------------------------------------------
  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      const dateString = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined;
      const data = await apiClient.visits.getAllVisits(
        page,
        limit,
        dateString,
        searchQuery,
        selectedVisitorForFilter?.id
      );
      setVisits(data.visits || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load visits:', error);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, selectedDate, searchQuery, selectedVisitorForFilter]);

  const loadActiveVisits = useCallback(async () => {
    try {
      const data = await apiClient.visits.getActiveVisits(1, 100);
      const activeMap: Record<string, boolean> = {};
      data.visits?.forEach((visit) => {
        if (visit?.visitor_id) activeMap[visit.visitor_id] = true;
      });
      setActiveVisits(activeMap);
    } catch (error) {
      console.error('Failed to load active visits:', error);
    }
  }, []);

  const loadActiveSubscriptions = useCallback(async () => {
    try {
      const subscriptions = await apiClient.getAllActiveSubscriptions();
      const map: Record<string, boolean> = {};
      subscriptions.forEach((sub) => {
        map[sub.visitorId] = true;
      });
      setActiveSubscriptions(map);
    } catch (error) {
      console.error('Failed to load active subscriptions:', error);
      setActiveSubscriptions({});
    }
  }, []);

  // Применение фильтра из location.state
  useEffect(() => {
    const applyFilterFromState = async () => {
      const state = location.state as { visitorId?: string; visitorName?: string } | null;
      if (state?.visitorId && !filterAppliedRef.current) {
        try {
          const visitor = await apiClient.visitors.getVisitor(state.visitorId);
          setSelectedDate(undefined);
          setSelectedVisitorForFilter(visitor);
          setPage(1);
          filterAppliedRef.current = true;
          navigate(location.pathname, { replace: true, state: {} });
        } catch (error) {
          console.error('Failed to load visitor from state:', error);
        }
      }
    };
    applyFilterFromState();
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  useEffect(() => {
    loadActiveVisits();
    loadActiveSubscriptions();
  }, [loadActiveVisits, loadActiveSubscriptions]);

  // Debounce поиска
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Периодическое обновление
  useEffect(() => {
    const interval = setInterval(() => {
      loadActiveVisits();
      loadActiveSubscriptions();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadActiveVisits, loadActiveSubscriptions]);

  // Обработчики фильтрации по посетителю
  const handleFilterByVisitor = useCallback(
    async (visitorSummary: Pick<Visitor, 'id' | 'name' | 'phone' | 'photoUrl'>) => {
      try {
        const fullVisitor = await apiClient.visitors.getVisitor(visitorSummary.id);
        setSelectedDate(undefined);
        setSelectedVisitorForFilter(fullVisitor);
        setPage(1);
      } catch (error) {
        console.error('Failed to load visitor details for filter:', error);
      }
    },
    []
  );

  const handleClearFilter = useCallback(() => {
    setSelectedVisitorForFilter(null);
    setSelectedDate(new Date());
    setPage(1);
  }, []);

  const handlePreviousDay = useCallback(() => {
    setSelectedDate((prev) => (prev ? subDays(prev, 1) : prev));
    setPage(1);
  }, []);

  const handleNextDay = useCallback(() => {
    setSelectedDate((prev) => (prev ? addDays(prev, 1) : prev));
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleShowVisitorCard = useCallback(async (visitorId: string) => {
    setLoadingVisitorCard(true);
    try {
      const fullVisitor = await apiClient.visitors.getVisitor(visitorId);
      setSelectedVisitor(fullVisitor);
      setVisitorDetailOpen(true);
    } catch (error) {
      console.error('Failed to load visitor details:', error);
    } finally {
      setLoadingVisitorCard(false);
    }
  }, []);

  const formatDuration = (checkIn: string, checkOut?: string | null) => {
    const start = new Date(checkIn).getTime();
    const end = checkOut ? new Date(checkOut).getTime() : Date.now();
    const minutes = Math.floor((end - start) / 60000);
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} ч ${mins} мин` : `${hours} ч`;
  };

  // Скелетон для таблицы
  const renderTableSkeleton = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Посетитель</TableCell>
            <TableCell>Вход</TableCell>
            <TableCell>Выход</TableCell>
            <TableCell>Длительность</TableCell>
            <TableCell align="right">Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(limit > 10 ? 10 : limit)].map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Box display="flex" alignItems="center" gap={2}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box>
                    <Skeleton width={120} />
                    <Skeleton width={80} />
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Skeleton width={60} />
                <Skeleton width={80} />
              </TableCell>
              <TableCell>
                <Skeleton width={60} />
                <Skeleton width={80} />
              </TableCell>
              <TableCell>
                <Skeleton width={50} />
              </TableCell>
              <TableCell align="right">
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Заголовок и активный фильтр по посетителю */}
      <Box display="flex" alignItems="center" flexWrap="wrap" mb={2}>
        <Typography variant="h4" fontWeight={700} gutterBottom sx={{ mr: 2 }}>
          История посещений
        </Typography>
        {selectedVisitorForFilter && (
          <Chip
            label={`Посетитель: ${selectedVisitorForFilter.name}`}
            onDelete={handleClearFilter}
            deleteIcon={<Clear />}
            color="primary"
            variant="outlined"
            sx={{ mb: 1 }}
          />
        )}
      </Box>

      {/* Панель фильтров (всегда видима) */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Поиск посетителей..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, maxWidth: 400 }}
            disabled={!!selectedVisitorForFilter}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Предыдущий день">
              <IconButton
                onClick={handlePreviousDay}
                size="small"
                sx={{ border: '1px solid', borderColor: 'divider' }}
                disabled={!selectedDate || !!selectedVisitorForFilter}
                aria-label="Предыдущий день"
              >
                <ArrowBackIosNew fontSize="small" />
              </IconButton>
            </Tooltip>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
              <DatePicker
                label="Дата"
                value={selectedDate ?? null}
                onChange={(newDate) => setSelectedDate(newDate || undefined)}
                referenceDate={new Date()}
                disabled={!!selectedVisitorForFilter}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 200 },
                  },
                }}
              />
            </LocalizationProvider>

            <Tooltip title="Следующий день">
              <IconButton
                onClick={handleNextDay}
                size="small"
                sx={{ border: '1px solid', borderColor: 'divider' }}
                disabled={!selectedDate || !!selectedVisitorForFilter}
                aria-label="Следующий день"
              >
                <ArrowForwardIos fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Chip label={`Всего записей: ${total}`} variant="outlined" />
        </Box>
      </Paper>

      {/* Основной контент: таблица или скелетон */}
      {loading ? (
        renderTableSkeleton()
      ) : visits.length > 0 ? (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Посетитель</TableCell>
                  <TableCell>Вход</TableCell>
                  <TableCell>Выход</TableCell>
                  <TableCell>Длительность</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visits.map((visit) => (
                  <TableRow key={visit.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar src={getImageSrc(visit.visitor.photoUrl)}>
                          {visit.visitor.name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">{visit.visitor.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {visit.visitor.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{safeFormatTime(visit.check_in)}</Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {safeFormatDate(visit.check_in)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {visit.check_out ? (
                        <>
                          <Typography variant="body2">{safeFormatTime(visit.check_out)}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {safeFormatDate(visit.check_out)}
                          </Typography>
                        </>
                      ) : (
                        <Chip label="В зале" size="small" color="primary" />
                      )}
                    </TableCell>
                    <TableCell>{formatDuration(visit.check_in, visit.check_out)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Показать все посещения">
                        <IconButton
                          onClick={() =>
                            handleFilterByVisitor({
                              id: visit.visitor.id,
                              name: visit.visitor.name,
                              phone: visit.visitor.phone,
                              photoUrl: visit.visitor.photoUrl,
                            })
                          }
                          color={
                            selectedVisitorForFilter?.id === visit.visitor.id
                              ? 'primary'
                              : 'default'
                          }
                          aria-label="Фильтр по посетителю"
                        >
                          <FilterList />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="QR-код и карточка">
                        <IconButton
                          onClick={() => handleShowVisitorCard(visit.visitor.id)}
                          disabled={loadingVisitorCard}
                          aria-label="Показать карточку посетителя"
                        >
                          {loadingVisitorCard ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <QrCode />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationBar
            count={total}
            page={page}
            rowsPerPage={limit}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            rowsPerPageOptions={PAGE_SIZES}
            labelRowsPerPage="Строк"
          />
        </Paper>
      ) : (
        // Улучшенное пустое состояние
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {selectedVisitorForFilter
              ? `Нет посещений ${selectedVisitorForFilter.name} ${
                  selectedDate ? `за ${safeFormatDate(selectedDate)}` : ''
                }`
              : selectedDate
                ? `Нет посещений за ${safeFormatDate(selectedDate)}`
                : 'Нет посещений'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClearFilter}
            sx={{ mt: 2 }}
          >
            Сбросить фильтры
          </Button>
        </Paper>
      )}

      {/* Диалог карточки посетителя */}
      <Dialog
        open={visitorDetailOpen}
        onClose={() => setVisitorDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
            <IconButton
              onClick={() => setVisitorDetailOpen(false)}
              size="small"
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'grey.100' },
              }}
              aria-label="Закрыть"
            >
              <Close />
            </IconButton>
          </Box>
          {selectedVisitor && (
            <VisitorCard
              visitor={selectedVisitor}
              mode="full"
              onEdit={() => {}}
              onDelete={() => {}}
              onShowHistory={() => setVisitorDetailOpen(false)}
              onCheckIn={() => {}}
              onCheckOut={() => {}}
              isCheckedIn={activeVisits[selectedVisitor.id] || false}
              hasActiveSubscription={activeSubscriptions[selectedVisitor.id] || false}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default VisitHistory;
